import React, { useState, useMemo, useCallback } from 'react';
import { useGetStepsAssignmentQuery, useAllStepsDataQuery } from 'js/queries';
import { filterStep5Proceed, objToArray } from './helpers.js';
import _flatten from 'lodash/flatten';
import _uniq from 'lodash/uniq';
import Loader from 'cccisd-loader';

const useGetLogicModelData = ({ deployment, metricspawn }) => {
    const [desiredOutcomeDataLocation, setDesiredOutcomeDataLocation] = useState(null);
    const stepsAssignment = useGetStepsAssignmentQuery(
        `stepsAssignment ${deployment.deploymentId}`,
        metricspawn.pawnId,
        deployment.deploymentId
    );

    const thereIsStep2Progress =
        stepsAssignment?.data?.data?.flows.deployment.assignment.surveyList[1].flow.flowProgress.status;
    const thereIsStep3Progress =
        stepsAssignment?.data?.data?.flows.deployment.assignment.surveyList[2].flow.flowProgress.status;
    const thereIsStep4Progress =
        stepsAssignment?.data?.data?.flows.deployment.assignment.surveyList[3].flow.flowProgress.status;
    const thereIsStep5Progress =
        stepsAssignment?.data?.data?.flows.deployment.assignment.surveyList[4].flow.flowProgress.status;

    const allStepsDataQuery = useAllStepsDataQuery(
        `allStepsData ${deployment.deploymentId}`,
        metricspawn.pawnId,
        deployment.deploymentId
    );

    const stepsData = useMemo(() => {
        const devTags = allStepsDataQuery?.data?.data?.roles?.metricspawn?.assignmentProgress?.devTags;
        if (!devTags) return null;

        // Helper function to structure alert data for compatibility
        const structureAlertsData = stepPrefix => {
            const alerts = {};
            const alertsResolve = {};

            Object.keys(devTags).forEach(key => {
                if (key.startsWith(stepPrefix)) {
                    if (key.includes('_resolve')) {
                        alertsResolve[key] = devTags[key];
                    } else {
                        alerts[key] = devTags[key];
                    }
                }
            });

            const stepMap = {
                s2_: 'step2',
                s3_: 'step3',
                s4_: 'step4',
                s5_: 'step5',
            };
            const stepName = stepMap[stepPrefix];

            return {
                data: {
                    data: {
                        roles: {
                            metricspawn: {
                                assignmentProgress: {
                                    [`${stepName}Alerts`]: alerts,
                                    [`${stepName}AlertsResolve`]: alertsResolve,
                                },
                            },
                        },
                    },
                },
            };
        };

        return {
            listVarsData: {
                data: {
                    data: {
                        roles: {
                            metricspawn: {
                                assignmentProgress: {
                                    devTags,
                                    // Extract specific data structures that LogicModel expects
                                    selectedOutcomes: Object.keys(devTags)
                                        .filter(key => key.includes('_selected_outcomes'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    proceed: Object.keys(devTags)
                                        .filter(key => key.includes('_proceed'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    selectedGoals: Object.keys(devTags)
                                        .filter(key => key.includes('_selected_goals'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    interventions: Object.keys(devTags)
                                        .filter(key => key.startsWith('step3_') && key.includes('_name'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    desiredOutcomes: Object.keys(devTags)
                                        .filter(key => key.includes('_desired_outcome_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    outcomesEvalMeasures: Object.keys(devTags)
                                        .filter(key => key.includes('_eval_measure'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    implementationQuality: Object.keys(devTags)
                                        .filter(key => key.includes('_yn') || key.includes('_text'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    monitorPopulationChanges: devTags.s6_monitor_population_changes_yn,
                                },
                            },
                        },
                    },
                },
            },
            step2Alerts: structureAlertsData('s2_'),
            step3Alerts: structureAlertsData('s3_'),
            step4Alerts: structureAlertsData('s4_'),
            step5Alerts: structureAlertsData('s5_'),
        };
    }, [allStepsDataQuery?.data]);

    // Create mock query objects to maintain compatibility
    const step2AlertsQuery = useMemo(
        () => ({
            data: stepsData?.step2Alerts?.data,
            isError: allStepsDataQuery.isError,
            isLoading: allStepsDataQuery.isLoading,
        }),
        [stepsData?.step2Alerts?.data, allStepsDataQuery.isError, allStepsDataQuery.isLoading]
    );

    const step3AlertsQuery = useMemo(
        () => ({
            data: stepsData?.step3Alerts?.data,
            isError: allStepsDataQuery.isError,
            isLoading: allStepsDataQuery.isLoading,
        }),
        [stepsData?.step3Alerts?.data, allStepsDataQuery.isError, allStepsDataQuery.isLoading]
    );

    const step4AlertsQuery = useMemo(
        () => ({
            data: stepsData?.step4Alerts?.data,
            isError: allStepsDataQuery.isError,
            isLoading: allStepsDataQuery.isLoading,
        }),
        [stepsData?.step4Alerts?.data, allStepsDataQuery.isError, allStepsDataQuery.isLoading]
    );

    const step5AlertsQuery = useMemo(
        () => ({
            data: stepsData?.step5Alerts?.data,
            isError: allStepsDataQuery.isError,
            isLoading: allStepsDataQuery.isLoading,
        }),
        [stepsData?.step5Alerts?.data, allStepsDataQuery.isError, allStepsDataQuery.isLoading]
    );

    const step3ListVarsQuery = useMemo(
        () => ({
            data: stepsData?.listVarsData?.data,
            isError: allStepsDataQuery.isError,
            isLoading: allStepsDataQuery.isLoading,
        }),
        [stepsData?.listVarsData?.data, allStepsDataQuery.isError, allStepsDataQuery.isLoading]
    );

    const step5ListVarsQuery = useMemo(
        () => ({
            data: stepsData?.listVarsData?.data,
            isError: allStepsDataQuery.isError,
            isLoading: allStepsDataQuery.isLoading,
        }),
        [stepsData?.listVarsData?.data, allStepsDataQuery.isError, allStepsDataQuery.isLoading]
    );

    const step6ListVarsQuery = useMemo(
        () => ({
            data: stepsData?.listVarsData?.data,
            isError: allStepsDataQuery.isError,
            isLoading: allStepsDataQuery.isLoading,
        }),
        [stepsData?.listVarsData?.data, allStepsDataQuery.isError, allStepsDataQuery.isLoading]
    );

    const isLoading = stepsAssignment.isLoading || allStepsDataQuery.isLoading;

    const allQueryDataReturned = stepsAssignment.data && allStepsDataQuery.data;

    const outcomes = useMemo(
        () => step3ListVarsQuery.data?.data.roles.metricspawn.assignmentProgress.selectedOutcomes,
        [step3ListVarsQuery.data]
    );

    const interventionsProceedStep3 = useMemo(
        () => step3ListVarsQuery.data?.data.roles.metricspawn.assignmentProgress.proceed || {},
        [step3ListVarsQuery.data]
    );

    const interventionsProceedStep5 = useMemo(
        () => step5ListVarsQuery.data?.data.roles.metricspawn.assignmentProgress.proceed || {},
        [step5ListVarsQuery.data]
    );

    const s3goals = useMemo(
        () => step3ListVarsQuery.data?.data.roles.metricspawn.assignmentProgress.selectedGoals || {},
        [step3ListVarsQuery.data]
    );

    const interventions = useMemo(
        () => step3ListVarsQuery.data?.data.roles.metricspawn.assignmentProgress.interventions || {},
        [step3ListVarsQuery.data]
    );

    const step6Outcomes = useMemo(
        () => step6ListVarsQuery.data?.data.roles.metricspawn.assignmentProgress,
        [step6ListVarsQuery.data]
    );

    const filteredGoals = useMemo(
        () =>
            filterStep5Proceed(
                s3goals || {},
                's3_',
                '_selected_goals',
                interventionsProceedStep5,
                thereIsStep5Progress
            ),
        [s3goals, interventionsProceedStep5, thereIsStep5Progress]
    );

    const selectedGoalsList = useMemo(
        () =>
            objToArray(filteredGoals || {})
                .filter(x => x !== 'MetricsDevTags')
                .filter(Boolean)
                .map(x => {
                    try {
                        return JSON.parse(x);
                    } catch (e) {
                        return null;
                    }
                })
                .filter(Boolean),
        [filteredGoals]
    );

    const step3FilteredOutcomes = useMemo(
        () =>
            filterStep5Proceed(
                outcomes || {},
                's3_',
                '_selected_outcomes',
                interventionsProceedStep5,
                thereIsStep5Progress
            ),
        [outcomes, interventionsProceedStep5, thereIsStep5Progress]
    );

    const filteredInterventions = useMemo(
        () =>
            objToArray(
                filterStep5Proceed(
                    interventions || {},
                    'step3_',
                    '_name',
                    interventionsProceedStep5,
                    thereIsStep5Progress
                ) || {}
            )
                .filter(x => x !== 'MetricsDevTags')
                .filter(Boolean),
        [interventions, interventionsProceedStep5, thereIsStep5Progress]
    );

    const getStep3Outcomes = useCallback(() => {
        let outcomesList = objToArray(step3FilteredOutcomes)
            .filter(x => x !== 'MetricsDevTags')
            .filter(Boolean);

        outcomesList = _flatten(outcomesList.map(x => JSON.parse(x)));
        return outcomesList;
    }, [step3FilteredOutcomes]);

    const step6DesiredOutcomes = useMemo(
        () =>
            objToArray(step6Outcomes?.desiredOutcomes || {})
                .filter(x => x !== 'MetricsDevTags')
                .filter(Boolean),
        [step6Outcomes]
    );

    const step6EvalMeasures = useMemo(
        () =>
            objToArray(step6Outcomes?.outcomesEvalMeasures || {})
                .filter(x => x !== 'MetricsDevTags')
                .filter(Boolean),
        [step6Outcomes]
    );

    const monitorPopulationChanges = useMemo(() => step6Outcomes?.monitorPopulationChanges, [step6Outcomes]);

    const getDesiredOutcomes = useCallback(
        interventionId => {
            if (!outcomes || !step6Outcomes?.desiredOutcomes) return [];

            const s3Outcomes = outcomes;
            const s6Outcomes = step6Outcomes.desiredOutcomes;
            const s6OutcomesList = [];

            const selectedOutcomesKey = 's3_' + interventionId + '_selected_outcomes';
            if (!s3Outcomes[selectedOutcomesKey]) return [];

            const s3OutcomesList = JSON.parse(s3Outcomes[selectedOutcomesKey]).map((outcome, i) => ({
                ['outcome_' + (i + 1)]: outcome,
            }));

            for (let x = 1; x <= 9; x++) {
                const desiredOutcomeKey = 's6_' + interventionId + '_desired_outcome_' + x;
                if (s6Outcomes[desiredOutcomeKey]) {
                    s6OutcomesList.push({
                        ['outcome_' + x]: s6Outcomes[desiredOutcomeKey],
                    });
                }
            }
            setDesiredOutcomeDataLocation(s6OutcomesList.length > 0 ? 'Step 6' : 'Step 2');

            return s6OutcomesList.length > 0 ? s6OutcomesList : s3OutcomesList;
        },
        [outcomes, step6Outcomes]
    );

    const getInterventionProceedIds = useMemo(() => {
        if (!interventionsProceedStep3 || !interventionsProceedStep5) return [];

        const step3ProceedKeys = Object.keys(interventionsProceedStep3).filter(key => {
            return interventionsProceedStep3[key] === '0';
        });
        return step3ProceedKeys
            .filter(key => interventionsProceedStep5['s5' + key.slice(2)] !== '99')
            .map(interventionKey => interventionKey.slice(3, 5));
    }, [interventionsProceedStep3, interventionsProceedStep5]);

    const getProcessEvalMeasures = useCallback(
        interventionId => {
            if (!step6Outcomes?.implementationQuality) return [];

            const metrics = objToArray(step6Outcomes.implementationQuality)
                .filter(x => x !== 'MetricsDevTags')
                .filter(Boolean);

            if (!metrics.some(x => x)) return [];

            const measures = [];
            const qualityChecks = [
                {
                    key: 's6_' + interventionId + '_intervention_reach_yn',
                    label: 'Intervention Reach',
                },
                {
                    key: 's6_' + interventionId + '_implementation_fidelity_yn',
                    label: 'Implementation Fidelity',
                },
                {
                    key: 's6_' + interventionId + '_participant_perceptions_yn',
                    label: 'Participant Perceptions',
                },
                {
                    key: 's6_' + interventionId + '_staff_perceptions_yn',
                    label: 'Staff Perceptions',
                },
                {
                    key: 's6_' + interventionId + '_adherence_to_workplan_yn',
                    label: 'Adherence to Workplan',
                },
                { key: 's6_' + interventionId + '_quality_other_yn', label: 'Other' },
            ];

            qualityChecks.forEach(({ key, label }) => {
                if (step6Outcomes.implementationQuality[key] === '1') {
                    measures.push(label);
                }
            });

            return measures;
        },
        [step6Outcomes]
    );

    const getOutcomeEvalMeasures = useCallback(
        interventionId => {
            if (!step6Outcomes?.outcomesEvalMeasures) return [];

            const evalMeasuresList = [];
            for (let x = 1; x <= 9; x++) {
                const measureKey = 's6_' + interventionId + '_desired_outcome_' + x + '_eval_measure';
                if (step6Outcomes.outcomesEvalMeasures[measureKey]) {
                    evalMeasuresList.push({
                        ['outcome_' + x]: step6Outcomes.outcomesEvalMeasures[measureKey],
                    });
                }
            }
            return evalMeasuresList;
        },
        [step6Outcomes]
    );

    const interventionsSorted = useMemo(() => {
        return getInterventionProceedIds.map(id => {
            const goalsKey = 's3_' + id + '_selected_goals';
            const nameKey = 'step3_' + id + '_name';

            return {
                key: id,
                title: interventions?.[nameKey] || '',
                goals: s3goals?.[goalsKey] ? JSON.parse(s3goals[goalsKey]) : [],
                outcomes: getDesiredOutcomes(id),
                processEvalMeasures: getProcessEvalMeasures(id),
                outcomeEvalMeasures: getOutcomeEvalMeasures(id),
            };
        });
    }, [
        getInterventionProceedIds,
        interventions,
        s3goals,
        getDesiredOutcomes,
        getProcessEvalMeasures,
        getOutcomeEvalMeasures,
    ]);

    const uniqueGoalList = useMemo(() => _uniq(selectedGoalsList.flat()), [selectedGoalsList]);

    const getOutcomesByGoal = useCallback(interventionDataList => {
        const outcomesList = [];
        // Iterate through each intervention associated with this Goal.
        // Get all unique outcomes associated with those interventions.
        interventionDataList.forEach(intervention => {
            intervention.outcomes.forEach(outcomeObj => {
                const outcomeInList = outcomesList.find(outcome => outcome.title === Object.values(outcomeObj)[0]);
                const outcomeEvalMeasures = intervention.outcomeEvalMeasures.find(
                    measure => measure[Object.keys(outcomeObj)[0]]
                );

                if (!outcomeInList) {
                    outcomesList.push({
                        key: Object.keys(outcomeObj)[0],
                        title: Object.values(outcomeObj)[0],
                        interventions: [{ id: intervention.key, title: intervention.title }],
                        processEvalMeasures: {
                            [intervention.key]: intervention.processEvalMeasures,
                        },
                        outcomeEvalMeasures: outcomeEvalMeasures ? Object.values(outcomeEvalMeasures) : null,
                    });
                } else {
                    if (!outcomeInList.interventions.find(x => x.id === intervention.key)) {
                        outcomeInList.interventions.push({
                            id: intervention.key,
                            title: intervention.title,
                        });
                    }
                    if (!outcomeInList.processEvalMeasures[intervention.key]) {
                        outcomeInList.processEvalMeasures[intervention.key] = intervention.processEvalMeasures;
                    }
                    const currOutcomeEvalMeasure = intervention.outcomeEvalMeasures.find(
                        measure => measure[Object.keys(outcomeObj)[0]]
                    );
                    const currOutcomeEvalMeasureValue = currOutcomeEvalMeasure
                        ? Object.values(currOutcomeEvalMeasure)[0]
                        : null;
                    if (
                        currOutcomeEvalMeasureValue &&
                        !outcomeInList.outcomeEvalMeasures?.includes(currOutcomeEvalMeasureValue)
                    ) {
                        // Ensure outcomeEvalMeasures is an array if it was null
                        if (!outcomeInList.outcomeEvalMeasures) {
                            outcomeInList.outcomeEvalMeasures = [];
                        }
                        outcomeInList.outcomeEvalMeasures.push(currOutcomeEvalMeasureValue);
                    }
                }
            });
        });

        return outcomesList;
    }, []);

    const outcomesByGoal = useMemo(
        () =>
            uniqueGoalList.map(goal => {
                const interventionsByGoal = interventionsSorted.filter(data => data.goals.includes(goal));
                return {
                    title: goal,
                    interventionData: interventionsByGoal,
                    outcomes: getOutcomesByGoal(interventionsByGoal),
                };
            }),
        [uniqueGoalList, interventionsSorted, getOutcomesByGoal]
    );

    if (isLoading || !allQueryDataReturned) {
        return <Loader loading type="inline" />;
    }

    return {
        stepsAssignment,
        thereIsStep2Progress,
        thereIsStep3Progress,
        thereIsStep4Progress,
        thereIsStep5Progress,
        step2AlertsQuery,
        step3AlertsQuery,
        step4AlertsQuery,
        step5AlertsQuery,
        loading: isLoading,
        isLoading,
        allQueryDataReturned,
        selectedGoalsList,
        filteredInterventions,
        getStep3Outcomes,
        step3Outcomes: getStep3Outcomes(),
        step6Outcomes,
        step6DesiredOutcomes,
        step6EvalMeasures,
        monitorPopulationChanges,
        interventionsSorted,
        outcomesByGoal,
        desiredOutcomeDataLocation,
    };
};

export default useGetLogicModelData;
